'use client';

import clsx from 'clsx';
import type { ForwardedRef } from 'react';
import { Link as RACLink, type LinkProps as RACLinkProps } from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { withStyleProps } from '../../with-style-props.js';
import type { NavigationProps, WithRequired } from '../types.js';
import type { BaseButtonProps, SingleIconButtonProps } from './types.js';

export interface IconLinkButtonProps
  extends
    Omit<BaseButtonProps, 'id'>,
    SingleIconButtonProps,
    WithRequired<NavigationProps, 'href'>,
    Pick<RACLinkProps, 'isDisabled' | 'onHoverStart' | 'onHoverEnd'> {}

function IconLinkButton(
  {
    icon,
    variant = 'secondary',
    tone = 'base',
    size = 'medium',
    isDisabled = false,
    fullWidth = false,
    UNSAFE_className,
    UNSAFE_style,
    ...props
  }: IconLinkButtonProps,
  ref: ForwardedRef<HTMLAnchorElement>,
) {
  return (
    <RACLink
      {...props}
      ref={ref}
      className={clsx('cim-icon-button', UNSAFE_className)}
      style={UNSAFE_style}
      data-variant={variant}
      data-tone={tone}
      data-size={size}
      data-full-width={fullWidth || undefined}
      isDisabled={isDisabled}
    >
      {icon}
    </RACLink>
  );
}

/**
 * Displays an icon-only button that allows users to navigate to another page or resource.
 *
 * See [button usage guidelines](https://ui.cimpress.io/components/button/).
 */
const _IconLinkButton = withStyleProps(forwardRef(IconLinkButton), 'IconLinkButton');

export { _IconLinkButton as IconLinkButton };
